import { useEffect } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Guard } from '../App';
import type { AuditEvent, OwnerCommand, Plan, Project, WorkerRoutingRow } from '../lib/data';
import { planHistory, planReviewModel, reviewablePlans, wasAnswered } from '../lib/plans';
import { proposalArchiveNote, type ProposalArchiveReason, type ProposalArchiveRow } from '../lib/proposal-archive';
import { planTaskRoutes, routesDiffer, routingLockLabel, type RoutingWorker } from '../lib/routing-view';
import { usePlanDecision } from '../lib/mutations';
import { DROP_LABEL, planDecisionOffer, RETRY_LABEL } from '../lib/command-chat';
import type { WorkerSeat } from '../lib/plan-routing';
import { HeldDraftChoices } from './HeldDraftChoices';

/**
 * The review itself: a drawer beside the operations desk with room to read a
 * plan properly, rather than a permanent card competing with the command box.
 * Escape closes it, as does the space beside it.
 *
 * Discuss plan is not a decision. It closes the drawer and puts a non-mutating
 * discussion prompt in the command box; the plan stays pending until the owner
 * Approves or Rejects. Do not call this revise until a structured supersede path exists.
 */
export function PlanDrawer({ client, guard, plans, projects, commands, routing, workers, seats, nowMs, onDecided, onDiscuss, onClose, archive = [] }: {
  client: SupabaseClient; guard: Guard; plans: readonly Plan[]; projects: readonly Project[];
  commands: readonly OwnerCommand[];
  /** The standing routing instruction per project, so approval says what it will obey. */
  routing: readonly WorkerRoutingRow[];
  /** The Workers, so a number can be shown as the computer the owner calls it. */
  workers: readonly RoutingWorker[];
  /** Each Worker's open session per project, so a held draft offers here what it offers in Command. */
  seats?: readonly WorkerSeat[] | undefined;
  nowMs: number;
  onDecided: () => Promise<void>;
  onDiscuss?: (plan: Plan, prefill: string) => void;
  onClose: () => void;
  /** Older proposals whose work finished or was replaced. They stay readable and offer no decision. */
  archive?: readonly ProposalArchiveRow[];
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const archivedIds = new Set(archive.map((row) => row.plan.id));
  const openPlans = plans.filter((plan) => !archivedIds.has(plan.id));
  const showDecisions = reviewablePlans(openPlans).length > 0;
  return (
    <>
      <button type="button" className="scrim" aria-label="Close the plan review" onClick={onClose} />
      <aside className="drawer" role="dialog" aria-label="Plans to approve">
        <header className="row spread drawer-head">
          <h2>Plans to approve</h2>
          <button type="button" className="link" onClick={onClose}>Close</button>
        </header>
        <div className="drawer-body">
          {(showDecisions || archive.length === 0) && (
            <PlansPanel client={client} guard={guard} plans={openPlans} projects={projects} commands={commands}
              routing={routing} workers={workers} seats={seats} nowMs={nowMs} onDecided={onDecided} onDiscuss={onDiscuss} spacious />
          )}
          <ProposalArchiveList rows={archive} />
        </div>
      </aside>
    </>
  );
}

/** Archived proposals the owner can open. No approve or reject. */
export function ProposalArchiveList({ rows }: { readonly rows: readonly ProposalArchiveRow[] }) {
  if (rows.length === 0) return null;
  return (
    <details className="proposal-archive" open>
      <summary>Archive ({rows.length})</summary>
      <ul className="list">
        {rows.map((row) => (
          <li key={row.plan.id}>
            <div className="stack">
              <span><span className="tag">archived</span> {row.command || row.plan.summary || 'Proposal'}</span>
              <small className="muted">{proposalArchiveNote(row.reason)}</small>
            </div>
          </li>
        ))}
      </ul>
    </details>
  );
}

/** Plans Brunel drafted from owner commands. Nothing runs until one is approved. */
export function PlansPanel({ client, guard, plans, projects, commands, routing, workers, seats, nowMs, onDecided, onDiscuss, spacious }: {
  client: SupabaseClient; guard: Guard; plans: readonly Plan[]; projects: readonly Project[];
  commands: readonly OwnerCommand[];
  routing: readonly WorkerRoutingRow[];
  workers: readonly RoutingWorker[];
  seats?: readonly WorkerSeat[] | undefined;
  nowMs: number;
  onDecided: () => Promise<void>;
  onDiscuss?: (plan: Plan, prefill: string) => void;
  spacious?: boolean;
}) {
  // A question Brunel answered is not a plan to decide on, so it is not offered
  // here - it would read as a failure, and the control that opens this review
  // has never counted it as one.
  const reviewable = reviewablePlans(plans);
  if (reviewable.length === 0) {
    return <p className="muted">No plans waiting. Send a command with a project selected and Brunel drafts a plan here.</p>;
  }
  return (
    <ul className="list">
      {reviewable.map((plan) => (
        <li key={plan.id}>
          <PlanCard client={client} guard={guard} plan={plan}
            projectName={projects.find((p) => p.id === plan.project_id)?.name ?? 'Project'}
            command={commands.find((c) => c.id === plan.command_id)?.content ?? ''}
            scopeWorkerNumber={routing.find((r) => r.project_id === plan.project_id)?.worker_number ?? null}
            workers={workers} seats={seats} nowMs={nowMs} onDecided={onDecided} onDiscuss={onDiscuss} spacious={spacious} />
        </li>
      ))}
    </ul>
  );
}

function PlanCard({ client, guard, plan, projectName, command, scopeWorkerNumber, workers, seats, nowMs, onDecided, onDiscuss, spacious }: {
  client: SupabaseClient; guard: Guard; plan: Plan; projectName: string; command: string;
  scopeWorkerNumber: number | null; workers: readonly RoutingWorker[];
  seats?: readonly WorkerSeat[] | undefined;
  nowMs: number;
  onDecided: () => Promise<void>;
  onDiscuss?: (plan: Plan, prefill: string) => void;
  spacious?: boolean;
}) {
  const { decide, busy, error, choices: refusedChoices } = usePlanDecision(client);
  // Plan decisions need the same verified owner session as approval decisions.
  const act = (decision: 'approve' | 'reject' | 'retry', workerNumber?: number) => guard('post_owner_command', async () => {
    if (await decide(plan.org_id, plan.id, decision, workerNumber)) await onDecided();
  });
  // A Worker or AI question held on this plan is answered by choosing; each choice is the approval.
  // Only the seats are read from the desk here: where the plan runs is worked out below.
  const offer = planDecisionOffer(plan, seats === undefined ? null : { projects: [], workers: [], running: [], seats });
  const refused = refusedChoices.length > 0;
  const choices = refused ? refusedChoices : offer?.choices ?? [];
  // Only a hold that still asks (Settings plan approval, a non-owner author) or an unheld draft
  // can be approved here; an owner's own command approved or queued itself on the server.
  const canApprove = offer?.decisions.includes('approve') ?? false;
  // A held draft names its decisions as Command does, with what each does beside it.
  const dropNote = offer?.note;
  const retryNote = offer?.retryNote;
  // An answered question is bookkeeping, not a drafting failure - never the retry path.
  if (wasAnswered(plan)) {
    return (
      <div className="stack">
        <span><span className="tag">answered</span> {projectName}</span>
        {command && <small className="muted">“{command}”</small>}
        {plan.summary.trim() !== '' && <small>{plan.summary}</small>}
        <small className="muted">You asked something rather than ordering work, so Brunel answered it instead of drafting a plan. No work created.</small>
      </div>
    );
  }
  if (plan.status === 'failed') {
    return (
      <div className="stack">
        <span><span className="tag">plan failed</span> {projectName}</span>
        {command && <small className="muted">“{command}”</small>}
        {plan.summary.trim() !== '' && <small>{plan.summary}</small>}
        <small className="muted">Brunel could not draft a plan ({plan.reason ?? 'unknown'}). Fix the cause and try again, or send the command again with more detail.</small>
        <div className="row">
          <button type="button" disabled={busy} onClick={() => void act('retry')}>Try again</button>
        </div>
        {error && <p role="alert">{error}</p>}
      </div>
    );
  }
  // Where this plan's work will actually go, worked out the same way the approval will work
  // it out. A plan that is going to one computer must never look like a plan that is going
  // wherever: that is the whole of the defect this shows.
  const routes = planTaskRoutes({ tasks: plan.tasks, command, scopeWorkerNumber, workers });
  const lock = routingLockLabel(scopeWorkerNumber, workers);
  const perTask = routesDiffer(routes);
  const target = routes[0] ?? null;
  const review = planReviewModel({ plan, projectName, command, nowMs });

  return (
    <div className="stack plan-consent">
      <strong className="plan-summary">{plan.summary || 'Proposed plan'}</strong>

      <section className="plan-you-asked" aria-label="You asked">
        <h3 className="plan-section-label">You asked</h3>
        {review.youAsked
          ? <p className="plan-you-asked-text">“{review.youAsked}”</p>
          : <p className="muted">The command that started this plan is no longer in view.</p>}
      </section>

      <ul className="plan-provenance" aria-label="Plan provenance">
        {review.provenanceLines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>

      <p className="plan-nothing-ran" role="note">{review.nothingRanYet}</p>

      <section className="plan-if-approve" aria-label="What happens if you approve">
        <h3 className="plan-section-label">What happens if you approve</h3>
        <dl className="plan-route">
          {!perTask && target !== null && (
            <div><dt>Runs on</dt><dd className="plan-target-prominent">{target.label}</dd></div>
          )}
          {perTask && <div><dt>Runs on</dt><dd>Each task goes to its own Worker, below</dd></div>}
          {lock !== null && <div><dt>Routing lock</dt><dd>{lock}</dd></div>}
        </dl>
        <ol className="plan-tasks">
          {plan.tasks.map((task, i) => (
            <li key={i}>
              <details open={spacious}>
                <summary>
                  {task.title}
                  {task.dependsOn && task.dependsOn.length > 0 && <span className="muted"> · after {task.dependsOn.map((d) => d + 1).join(', ')}</span>}
                  {perTask && <span className="task-target"> · {routes[i]?.label ?? 'Any idle Worker'}</span>}
                </summary>
                <p className="instructions">{task.instructions}</p>
              </details>
            </li>
          ))}
        </ol>
      </section>

      <HeldDraftChoices choices={choices} prompt={refused ? undefined : offer?.choicePrompt} busy={busy}
        onChoose={(workerNumber) => void act('approve', workerNumber)} />
      <div className="row wrap plan-actions">
        {canApprove && (retryNote === undefined
          ? <button type="button" className="primary" disabled={busy} onClick={() => void act('approve')}>Approve plan</button>
          : <button type="button" disabled={busy} onClick={() => void act('approve')}>{RETRY_LABEL}</button>)}
        <button type="button" disabled={busy} onClick={() => void act('reject')}>{dropNote === undefined ? 'Reject' : DROP_LABEL}</button>
        {onDiscuss && (
          <button type="button" className="link plan-discuss" disabled={busy}
            onClick={() => onDiscuss(plan, review.discussPrefill)}>
            Discuss plan
          </button>
        )}
      </div>
      {retryNote !== undefined && <small className="muted">{retryNote}</small>}
      {dropNote !== undefined && <small className="muted">{dropNote}</small>}
      {error && <p role="alert">{error}</p>}
    </div>
  );
}

/** Where each plan came from and what happened to it, newest first. */
export function PlanHistoryPanel({ plans, projects, commands, audit, nowMs, archived }: {
  plans: readonly Plan[]; projects: readonly Project[];
  commands: readonly OwnerCommand[]; audit: readonly AuditEvent[]; nowMs: number;
  /** Same classification the needs-you counts use. Absent, every proposed plan keeps its own label. */
  archived?: ReadonlyMap<string, ProposalArchiveReason>;
}) {
  const rows = planHistory(plans, commands, audit, nowMs);
  if (rows.length === 0) return <p className="muted">No plans yet.</p>;
  return (
    <ul className="list">
      {rows.map((row) => {
        const reason = archived?.get(row.id);
        const label = reason === undefined ? row.statusLabel : 'archived';
        return (
          <li key={row.id}>
            <div className="stack">
              <span className="row spread">
                <span><span className="tag">{label}</span> {projects.find((p) => p.id === row.projectId)?.name ?? 'Project'}</span>
                {row.createdLabel && <small className="muted">{row.createdLabel}</small>}
              </span>
              {row.command && <small className="muted">“{row.command}”</small>}
              {row.summary && <small>{row.summary}</small>}
              {reason !== undefined && <small className="muted">{proposalArchiveNote(reason)}</small>}
              {row.answered && <small className="muted">You asked something rather than ordering work, so Brunel answered it instead of drafting a plan.</small>}
              {row.reason && row.statusLabel === 'expired' && <small className="muted">{row.reason}</small>}
              {row.reason && row.statusLabel !== 'expired' && <small className="muted">The planner said: {row.reason}</small>}
              {row.retries > 0 && <small className="muted">Asked again {row.retries === 1 ? 'once' : `${row.retries} times`}</small>}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
